/**
 * Helpers shared by the tools LIST page and the tool DETAIL page. They were
 * local to Tools.tsx until the detail page existed; two copies of the error
 * vocabulary or the params check would drift, so they moved here whole.
 */
import { ApiError } from '@/api'

/** Server error codes for the tool CRUD, as sentences a person can act on. */
export function toolMessageFor(e: unknown): string {
  if (!(e instanceof ApiError)) return e instanceof Error ? e.message : String(e)
  switch (e.code) {
    case 'read_only':
      return 'Your role can read this but not change it.'
    case 'name_taken':
      // The server's message already says whether the holder is live or
      // revoked; nothing useful to append.
      return e.message
    case 'tool_limit':
      return e.message
    case 'revoked':
      return 'This tool was revoked (possibly from another tab) and cannot be edited.'
    case 'bad_tool_definition':
      return `The server refused the definition: ${e.message}`
    case 'bad_field':
      return `The server refused a field: ${e.message}`
    case 'no_such_tool':
      return 'There is no tool with that id — it may never have existed. Revoked tools stay on this page, so a broken link is the likelier story.'
    default:
      return e.message
  }
}

/** The params textarea, checked before anything is sent. */
export function parseParams(text: string): { ok: Record<string, unknown> } | { err: string } {
  const trimmed = text.trim()
  if (trimmed === '') {
    return { err: 'The parameters schema is required. A tool with no arguments still needs {"type": "object", "properties": {}}.' }
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch (e) {
    return { err: `Not valid JSON: ${e instanceof Error ? e.message : String(e)}` }
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { err: 'The schema must be a JSON object — an array or a bare value cannot describe arguments.' }
  }
  return { ok: parsed as Record<string, unknown> }
}

/** The host of a stored URL, for lists. The full URL can carry a path with a
 *  token in it, so a list shows where a tool goes, not the whole address. */
export function hostOf(url: string): string {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

/**
 * A fill-in-the-blanks args object from the tool's own JSON Schema, so the
 * test box opens with the right keys already typed. Values are the emptiest
 * member of each declared type — a skeleton, not sample data.
 */
export function skeletonFor(parameters: Record<string, unknown>): string {
  const props = parameters['properties']
  const out: Record<string, unknown> = {}
  if (typeof props === 'object' && props !== null) {
    for (const [key, spec] of Object.entries(props as Record<string, unknown>)) {
      const t = typeof spec === 'object' && spec !== null ? (spec as { type?: unknown }).type : undefined
      out[key] = t === 'number' || t === 'integer' ? 0 : t === 'boolean' ? false : t === 'array' ? [] : t === 'object' ? {} : ''
    }
  }
  return JSON.stringify(out, null, 2)
}
